'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import type { CommunityMember } from '@zealy/queries';
import type { GetQuestOutput } from '@zealy/utils';
import { Button, CONDITION_ICONS } from '@zealy/design-system';
import { CheckCircle2Filled, DiscordBrand, TwitterFilled } from '@zealy/icons';
import {
  useAuthenticatedUser,
  useCommunityDiscordRoles,
  useNFTCollectionMetadataBatch,
} from '@zealy/queries';

import { ConnectButton as ConnectToZealyButton } from '#components/JoinCommunityBar/ConnectButton';
import { ConnectButton } from '#features/Wallets/EVM/ConnectButton';
import { openOauth } from '#hooks/useOAuth2';
import { useQuestFromQuestboard } from '#hooks/useQuestFromQuestboard';

import { Base } from './StateBase';

type Matcher = NonNullable<GetQuestOutput['conditions']>[number];

const CHECK_ICON = <CheckCircle2Filled className="text-success-primary" />;

export const LockedQuest = ({
  quest,
  questId,
  moduleId,
}: {
  quest: GetQuestOutput;
  questId: string;
  moduleId: string;
}) => {
  const user = useAuthenticatedUser<CommunityMember>();
  const pathname = usePathname();

  const conditions = quest.conditions || [];

  // Get all quest ids from conditions
  const ids = (conditions
    ?.filter(matcher => matcher.type === 'quest' && matcher.value)
    .map(matcher => matcher.value) ?? []) as string[];

  const nftsContractAddresses = (conditions
    ?.filter(matcher => matcher.type === 'nft' && (matcher as any).contractAddress)
    .map(matcher => (matcher as any).contractAddress) ?? []) as string[];

  const nftsContractMetadata = useNFTCollectionMetadataBatch(nftsContractAddresses);
  const quests = useQuestFromQuestboard(ids);

  const includesRoles = conditions?.some(matcher => matcher.type === 'role');
  const roles = useCommunityDiscordRoles(undefined, { enabled: includesRoles });

  // Enhance conditions with extra data
  const enhancedConditions = conditions?.map(matcher => {
    if (matcher.type === 'quest' && quests[matcher.value]) {
      return {
        ...matcher,
        extraData: quests[matcher.value],
      };
    }
    if (matcher.type === 'nft') {
      const metadata = nftsContractMetadata.data?.find(
        contract =>
          contract.address.toLowerCase() === (matcher as any).contractAddress?.toLowerCase(),
      );
      return {
        ...matcher,
        extraData: metadata,
      };
    }
    if (matcher.type === 'role' && roles.data) {
      const role = roles.data.find(({ id }) => id === matcher.value);
      return {
        ...matcher,
        extraData: role,
      };
    }
    return matcher;
  });

  const renderCondition = (matcher: (typeof enhancedConditions)[0], index: number) => {
    // Generate label based on condition type
    let label = '';
    const value =
      matcher.type === 'date' ? new Date(matcher.value).toLocaleDateString() : matcher.value;

    if (matcher.type === 'quest') {
      const questName = (matcher as any).extraData?.name || 'Deleted quest';
      label = `Complete quest "${questName}"`;
    } else if (matcher.type === 'role') {
      const roleName = (matcher as any).extraData?.name || 'Deleted role';
      label = `Have Discord role "${roleName}"`;
    } else if (matcher.type === 'nft') {
      const nftName = (matcher as any).extraData?.name || 'NFT';
      label = `Hold ${nftName}`;
    } else if (matcher.type === 'twitterFollowers') {
      label = `Have at least ${value} Twitter followers`;
    } else if (matcher.type === 'twitterAccountAge') {
      if (matcher.operator === '>') {
        label = `X/Twitter account older than ${value} days`;
      } else {
        label = `X/Twitter account younger than ${value} days`;
      }
    } else if (matcher.type === 'level') {
      label = `Reach level ${value}`;
    } else if (matcher.type === 'date') {
      if (matcher.operator === '>') {
        label = `Wait until ${value}`;
      } else {
        label = `Complete before ${value}`;
      }
    }

    const rootPath = pathname?.substring(0, pathname.indexOf('/questboard'));

    const href =
      matcher.type === 'quest' && (matcher as any).extraData?.categoryId
        ? `${rootPath}/questboard/${(matcher as any).extraData.categoryId}/${matcher.value}`
        : undefined;

    if (matcher.type === 'twitterAccountAge' && !user.data) {
      return <ConnectToZealyButton key={`${matcher.type}-${index}`} className="w-full" />;
    }

    if (matcher.type === 'nft' && !user.data?.addresses?.['eth-mainnet'])
      return (
        <ConnectButton
          key={`${matcher.type}-${index}`}
          blockchain="eth-mainnet"
          buttonProps={{ className: 'w-full', color: 'default' }}
        />
      );

    if (
      matcher.type === 'twitterFollowers' &&
      !user.data?.accounts?.find(a => a.accountType === 'twitter')
    ) {
      return (
        <Button
          key={`${matcher.type}-${index}`}
          variant="muted"
          className="w-full whitespace-pre-wrap"
          color="default"
          onClick={() => openOauth('twitter')}
          leftIcon={<TwitterFilled />}
        >
          Connect Twitter
        </Button>
      );
    }

    if (
      matcher.type === 'twitterAccountAge' &&
      !user.data?.accounts?.find(a => a.accountType === 'twitter')
    ) {
      return (
        <Button
          key={`${matcher.type}-${index}`}
          variant="muted"
          className="w-full whitespace-pre-wrap"
          color="default"
          onClick={() => openOauth('twitter')}
          leftIcon={<TwitterFilled />}
        >
          Connect Twitter
        </Button>
      );
    }

    if (matcher.type === 'role' && !user.data?.accounts?.find(a => a.accountType === 'discord')) {
      return (
        <Button
          key={`${matcher.type}-${index}`}
          variant="muted"
          className="w-full whitespace-pre-wrap"
          color="default"
          onClick={() => openOauth('discord')}
          leftIcon={<DiscordBrand />}
        >
          Connect Discord
        </Button>
      );
    }

    return (
      <>
        {/* Desktop version */}
        <Button
          key={`${matcher.type}-${index}-desktop`}
          variant="muted"
          className="w-full !justify-start hidden md:flex"
          isDisabled={matcher.fulfilled}
          leftIcon={CONDITION_ICONS[matcher.type]}
          rightIcon={matcher.fulfilled ? CHECK_ICON : undefined}
          as={href ? Link : undefined}
          href={href}
        >
          {label}
        </Button>
        {/* Mobile version */}
        <div
          key={`${matcher.type}-${index}-mobile`}
          className="w-full flex md:hidden items-center  p-200 rounded-lg bg-surface-secondary gap-button-md px-button-md border-border text-sm button-md text-button-onneutral-muted-primary border-button-neutral-muted  bg-button-neutral-muted border"
        >
          <div className="shrink-0">{CONDITION_ICONS[matcher.type]}</div>
          <div className="flex-1">{label}</div>
          {matcher.fulfilled && <div className="shrink-0">{CHECK_ICON}</div>}
        </div>
      </>
    );
  };

  const unfulfilledConditions = enhancedConditions?.filter(c => !c.fulfilled) || [];
  const operator = quest.conditionOperator || 'AND';

  const description =
    unfulfilledConditions.length > 1
      ? `Complete ${operator === 'AND' ? 'all' : 'one'} of the following conditions`
      : null;

  return (
    <Base
      title="Quest Locked"
      description="You need to meet certain requirements to unlock this quest"
      mascot="crying"
    >
      <div className="flex flex-col gap-300 w-full">
        <div className="flex flex-col gap-150">
          <p className="text-sm font-semibold text-primary text-center">
            How to unlock this quest?
          </p>
          {description && <p className="text-xs text-secondary text-center">{description}</p>}
        </div>
        <div className="flex flex-col gap-150 w-full">
          {unfulfilledConditions.map(renderCondition)}
        </div>
      </div>
    </Base>
  );
};
